import React, { Component } from 'react';
import _ from 'lodash';
import { Grid } from 'semantic-grab-ui';
import './MetricsStatsTable.scss';
import { Icon, Tooltip } from 'grab-ui';
import { printNumber, printPercentage } from 'omega-rtc/views/trident-campaigns/metrics/utils';
import { METRICS_CELL_ITEMS } from 'omega-rtc/constants/ucd';
import { SCORE_CATEGORY, SCORE_COLOR } from 'omega-rtc/constants/metrics';

const COLUMN = METRICS_CELL_ITEMS;

type Props = {
  title: string, // the text displayed on the leftmost column
  items: Array, // array of object to be shown on the cell. [{val:7800, percentage:98.5, uplift:-3.41}]
  width: number, // width of each column
  tooltip: string, // tooltip on the title
  showUplift: boolean, // to show uplift on the screen
};
const DEFAULT_TEXT_COLOR = '#363a45';

class MetricsStatsRow extends Component {
  props: Props;


  getBenchmarkTooltipText(benchmarkScore: string, benchmarkRate: number, uplift: number) {
    switch (benchmarkScore) {
      case SCORE_CATEGORY.great:
        return `90th percentile: ${benchmarkRate}%. The metric is ${uplift}pp higher than the 90th percentile of other similar campaigns`;
      case SCORE_CATEGORY.good:
        return `90th percentile: ${benchmarkRate}%. The metric is ${Math.abs(uplift)}pp lower than the 90th percentile of other similar campaigns`;
      case SCORE_CATEGORY.bad:
        return `Average: ${benchmarkRate}%. The metric is ${Math.abs(uplift)}pp lower than the average of other similar campaigns`;
      default:
        return '';
    }
  }

  constructColumn(item: Object, showUplift: boolean, columnIndex: number) {
    const number = printNumber(_.get(item, COLUMN.val, 0));
    const percentage = _.get(item, COLUMN.percentage, 0);
    const uplift = _.get(item, COLUMN.uplift, 0);
    const { benchmarkScore, benchmarkRate } = item;
    let color = DEFAULT_TEXT_COLOR;

    // Hide uplift if benchmark score is defined but benchmark rate is 0
    if (benchmarkScore) {
      if (benchmarkRate !== 0) {
        color = SCORE_COLOR[benchmarkScore];
      } else {
        showUplift = false;
      }
    }

    return (
      <div style={{ color }}>
        <div>
          {number}
          {percentage !== 0 && ` ${printPercentage(percentage)}`}
        </div>
        {showUplift && (!_.isEmpty(benchmarkScore) || columnIndex !== 0) && (
          this.renderUpliftPercentage(uplift, benchmarkScore, benchmarkRate)
        )}
      </div>
    );
  }

  render() {
    const { title, items, width, tooltip, showUplift } = this.props;
    return (<Grid.Row>
      <Grid.Column width={width}>
        {title}
        {!_.isEmpty(tooltip) && (
          <Tooltip title={tooltip}>
            <div className={'tooltip-holder'}>
              <Icon type="grabicon-information-02" />
            </div>
          </Tooltip>
        )}
      </Grid.Column>
      {_.map(items, (item, index) => {
        const isHide = _.get(item, COLUMN.hide, false);
        return (<Grid.Column key={index} width={width} className={showUplift ? 'rate-column' : ''}>
          {!isHide && (this.constructColumn(item, showUplift, index))}
        </Grid.Column>);
      })
      }
    </Grid.Row>);
  }

  renderUpliftPercentage(value, benchmarkScore, benchmarkRate) {
    const incrementRate = value.toFixed(2);
    const color = SCORE_COLOR[benchmarkScore] || (incrementRate >= 0 ? '#00B14F' : '#F55538');
    const arrow = incrementRate >= 0 ? '▲' : '▼';

    const incrementDisplay = (<div style={{ color }} className="increment-percentage">
      <span>{arrow}</span> {Math.abs(incrementRate)}pp
    </div>);
    const hasBenchmark = benchmarkRate && !_.isEmpty(benchmarkScore);
    return !hasBenchmark
      ? incrementDisplay
      : (
        <Tooltip
          title={this.getBenchmarkTooltipText(benchmarkScore, benchmarkRate.toFixed(2), incrementRate)}
          placement={'bottomRight'}
          classname={'benchmark-tooltip'}
        >
          {incrementDisplay}
        </Tooltip>
      );
  }
}


export default MetricsStatsRow;
